(() => {
  'use strict';

  if (customElements.get('difiney-adjustment')) return;

  class DifineyAdjustment extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.initFrame) return;
      // Wait for child markup when an already-defined element is inserted by the editor.
      this.initFrame = requestAnimationFrame(() => {
        this.initFrame = null;
        this.initialize();
      });
    }

    initialize() {
      if (this.controller || !this.isConnected) return;
      this.radios = Array.from(this.querySelectorAll('[data-adjustment-position]'));
      this.panels = Array.from(this.querySelectorAll('[data-adjustment-guidance-panel]'));
      this.range = this.querySelector('[data-adjustment-range]');
      this.rangeShell = this.querySelector('[data-adjustment-range-shell]');
      if (!this.radios.length || !this.panels.length) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.addEventListener('change', (event) => {
        if (this.radios.includes(event.target) || event.target === this.range) this.select(event.target.value);
      }, { signal });
      this.addEventListener('input', (event) => {
        if (event.target === this.range) this.select(event.target.value);
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const isRadio = this.radios.includes(event.target);
        if (!isRadio && event.target !== this.range) return;
        const current = Number(this.dataset.adjustmentValue) || 1;
        const destinations = { Home: 1, End: 6, ArrowLeft: current - 1, ArrowDown: current - 1, ArrowRight: current + 1, ArrowUp: current + 1 };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        this.select(destinations[event.key]);
        if (isRadio) this.radios.find((radio) => radio.checked).focus({ preventScroll: true });
      }, { signal });

      if (this.range && this.rangeShell) {
        this.range.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
          this.rangeShell.classList.add('is-dragging');
          this.range.setPointerCapture(event.pointerId);
        }, { signal });
        const endDrag = (event) => {
          this.rangeShell.classList.remove('is-dragging');
          if (this.range.hasPointerCapture(event.pointerId)) this.range.releasePointerCapture(event.pointerId);
          this.select(this.range.value);
        };
        this.range.addEventListener('pointerup', endDrag, { signal });
        this.range.addEventListener('pointercancel', endDrag, { signal });
        this.range.addEventListener('lostpointercapture', () => this.rangeShell.classList.remove('is-dragging'), { signal });
      }
      this.select(1);
      this.initializeVideo(signal);
    }

    initializeVideo(signal) {
      const video = this.querySelector('video');
      if (!video) return;
      this.video = video;
      this.videoVisible = false;
      video.controls = false;
      // Native autoplay would override preload="none" before the section is near.
      // Playback still starts automatically when this instance enters the viewport.
      video.autoplay = false;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.pause();
      let playbackPending = false;
      let playbackInterrupted = false;

      const prepareVideo = () => {
        if (signal.aborted || !this.isConnected || document.hidden) return;
        // Keep the loaded source and buffer across scrolling/editor reconnects.
        // Repeated load() calls would reset playback and abort in-flight requests.
        if (video.dataset.adjustmentPrepared === 'true') return;
        video.dataset.adjustmentPrepared = 'true';
        video.preload = 'auto';
        if (video.readyState === 0) video.load();
        if (this.videoPreloadObserver) this.videoPreloadObserver.disconnect();
        this.videoPreloadObserver = null;
      };

      const shouldPlay = () => !signal.aborted && this.isConnected && this.videoVisible && !document.hidden;
      const syncPlayback = () => {
        if (signal.aborted) return;
        video.autoplay = shouldPlay();
        if (!shouldPlay()) {
          playbackInterrupted = playbackPending;
          video.pause();
          return;
        }
        prepareVideo();
        if (playbackPending || !video.paused) return;
        video.muted = true;
        try {
          const playback = video.play();
          if (playback && typeof playback.then === 'function') {
            playbackPending = true;
            playback.then(() => {
              // An old editor generation must not pause a reconnected video.
              if (!signal.aborted && !shouldPlay()) video.pause();
            }).catch(() => {
              // Browser autoplay restrictions can be retried at the next visibility/canplay event.
            }).finally(() => {
              playbackPending = false;
              if (playbackInterrupted) {
                playbackInterrupted = false;
                if (shouldPlay()) syncPlayback();
              }
            });
          }
        } catch (_) {
          playbackPending = false;
        }
      };
      video.addEventListener('canplay', syncPlayback, { signal });
      document.addEventListener('visibilitychange', () => {
        if (this.videoNearViewport) prepareVideo();
        syncPlayback();
      }, { signal });

      if (typeof IntersectionObserver !== 'undefined') {
        this.videoNearViewport = false;
        if (video.dataset.adjustmentPrepared !== 'true') {
          this.videoPreloadObserver = new IntersectionObserver((entries) => {
            if (signal.aborted) return;
            entries.forEach((entry) => {
              if (entry.target !== video) return;
              this.videoNearViewport = entry.isIntersecting;
              if (this.videoNearViewport) prepareVideo();
            });
          }, { rootMargin: '800px 0px', threshold: 0 });
          this.videoPreloadObserver.observe(video);
        }
        this.videoObserver = new IntersectionObserver((entries) => {
          if (signal.aborted) return;
          entries.forEach((entry) => {
            if (entry.target !== video) return;
            this.videoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
            syncPlayback();
          });
        }, { threshold: [0, 0.1] });
        this.videoObserver.observe(video);
      } else {
        this.videoNearViewport = true;
        this.videoVisible = true;
        syncPlayback();
      }
    }

    select(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return;
      const position = Math.min(6, Math.max(1, Math.round(number)));
      const selected = this.radios.find((radio) => radio.value === String(position));
      if (!selected) return;
      const group = position === 1 ? 'low' : position <= 4 ? 'middle' : 'maximum';

      this.radios.forEach((radio) => { radio.checked = radio === selected; });
      if (this.range) {
        this.range.value = String(position);
        this.range.setAttribute('aria-valuetext', `${position} ${this.range.dataset.adjustmentUnit || 'mm'}`);
      }
      if (this.rangeShell) this.rangeShell.style.setProperty('--adjustment-fraction', String((position - 1) / 5));
      this.panels.forEach((panel) => {
        const active = panel.dataset.adjustmentGuidancePanel === group;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.dataset.adjustmentValue = selected.value;
    }

    disconnectedCallback() {
      if (this.initFrame) cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
      if (this.controller) this.controller.abort();
      this.controller = null;
      if (this.rangeShell) this.rangeShell.classList.remove('is-dragging');
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.videoPreloadObserver) this.videoPreloadObserver.disconnect();
      this.videoObserver = null;
      this.videoPreloadObserver = null;
      this.videoVisible = false;
      this.videoNearViewport = false;
      const video = this.querySelector('video');
      if (video) {
        video.autoplay = false;
        video.pause();
      }
    }
  }

  customElements.define('difiney-adjustment', DifineyAdjustment);
})();
